/**
 * OrgMemberProfileModal 컴포넌트 (V5.17)
 *
 * 조직원관리에서 직원 이름 클릭 시 표시되는 읽기 전용 프로필 카드 모달
 *
 * **기능:**
 * - 직원 상세 정보 조회 (수정 불가)
 * - ESC 키 / 배경 클릭 / X 버튼으로 닫기
 * - 로딩 스피너 및 에러 메시지 표시
 *
 * **표시 섹션:**
 * 1) 인적사항: 이름, 사번, 이메일, 생년월일 + **프로필 사진 (V55.13)**
 * 2) 직무: 직위, 직책
 * 3) 고용/계약: 고용형태, 재직상태, 입사일 등
 * 4) 학력/기타: 학력, 전공, 자격증, 교육이수, 비고
 * 5) 연락처: 전화번호, 주소
 *
 * **V55.13:**
 * - 인적사항 섹션에 프로필 사진 표시 (읽기 전용)
 * - EmployeePhotoSection과 동일한 3:4 비율, 스타일
 * - photoPath 기반 getDownloadURL 사용
 */

import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertCircle, User, Briefcase, Calendar, GraduationCap, Phone } from 'lucide-react';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/config';
import { useOrgMemberProfile, OrgMemberProfileCardData } from '../../../shared/hooks/useOrgMemberProfile';
import { getEducationLevelLabelSafe } from '../../../shared/utils/educationLevel';

// V55.13: 사진 영역 크기 (3:4 비율) - EmployeePhotoSection과 동일
const PHOTO_WIDTH = 120;
const PHOTO_HEIGHT = 160;

/**
 * Props
 */
export interface OrgMemberProfileModalProps {
  isOpen: boolean;
  targetUserId: string | null;
  onClose: () => void;
}

/**
 * 재직상태 라벨 변환
 */
function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: '재직',
    on_leave: '휴직',
    leave: '휴직',
    terminated: '퇴사',
  };
  return labels[status] || status || '-';
}

/**
 * 값이 비어있으면 '-' 반환
 */
function displayValue(value: string | null | undefined): string {
  if (!value || value.trim() === '') return '-';
  return value;
}

/**
 * V55.13: 읽기 전용 프로필 사진 컴포넌트
 * - EmployeePhotoSection과 동일한 스타일
 * - 업로드/삭제 버튼 없음
 * - photoPath 기반 getDownloadURL 사용
 */
function ReadOnlyEmployeePhoto({ photoPath, employeeName }: { photoPath: string; employeeName: string }) {
  const [resolvedURL, setResolvedURL] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!photoPath || !storage) {
      setResolvedURL(undefined);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const storageRef = ref(storage, photoPath);
    getDownloadURL(storageRef)
      .then((url) => {
        setResolvedURL(url);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('[ReadOnlyEmployeePhoto] getDownloadURL 실패:', error);
        setResolvedURL(undefined);
        setIsLoading(false);
        setHasError(true);
      });
  }, [photoPath]);

  // 사진 컨테이너 스타일 (EmployeePhotoSection과 동일)
  const containerStyle: React.CSSProperties = {
    width: `${PHOTO_WIDTH}px`,
    height: `${PHOTO_HEIGHT}px`,
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid var(--color-gray-200)',
    background: 'var(--color-gray-50)',
    flexShrink: 0,
    position: 'relative',
  };

  const photoStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const placeholderStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-gray-100)',
  };

  const loadingStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.8)',
  };

  return (
    <div style={containerStyle}>
      {resolvedURL && !hasError ? (
        <img
          src={resolvedURL}
          alt={`${employeeName} 프로필 사진`}
          style={photoStyle}
          onError={() => setHasError(true)}
        />
      ) : (
        <div style={placeholderStyle}>
          <User size={48} color="var(--color-gray-400)" />
        </div>
      )}
      {isLoading && (
        <div style={loadingStyle}>
          <Loader2 size={24} color="var(--color-primary)" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      )}
    </div>
  );
}

/**
 * 섹션 제목 컴포넌트
 */
function SectionTitle({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: '1px solid var(--color-gray-200)',
    }}>
      <Icon size={16} color="var(--color-gray-500)" />
      <span style={{
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--color-gray-700)',
      }}>{title}</span>
    </div>
  );
}

/**
 * 항목 행 컴포넌트 (Label: Value)
 */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex',
      marginBottom: '8px',
    }}>
      <span style={{
        width: '120px',
        flexShrink: 0,
        fontSize: '13px',
        color: 'var(--color-gray-500)',
      }}>{label}</span>
      <span style={{
        flex: 1,
        fontSize: '13px',
        color: 'var(--color-gray-800)',
      }}>{value}</span>
    </div>
  );
}

/**
 * 프로필 내용 렌더링
 */
function ProfileContent({ profile }: { profile: OrgMemberProfileCardData }) {
  return (
    <div style={{ padding: '0 24px 24px 24px' }}>
      {/* 1) 인적사항 + 프로필 사진 (V55.13) */}
      <section style={{ marginBottom: '24px' }}>
        <SectionTitle icon={User} title="인적사항" />
        {/* V55.13: 좌측 사진 + 우측 정보 레이아웃 */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          {/* 좌측: 프로필 사진 (V55.13) */}
          <ReadOnlyEmployeePhoto
            photoPath={profile.photoPath}
            employeeName={profile.nameKo || '직원'}
          />
          {/* 우측: 인적사항 정보 (사진 높이 기준 세로 중앙 정렬) */}
          <div style={{ flex: 1 }}>
            <InfoRow label="이름 (한글)" value={displayValue(profile.nameKo)} />
            <InfoRow label="사번" value={displayValue(profile.employeeNumber)} />
            <InfoRow label="이메일" value={displayValue(profile.email)} />
            <InfoRow label="생년월일" value={displayValue(profile.birthDate)} />
          </div>
        </div>
      </section>

      {/* 2) 직무 */}
      <section style={{ marginBottom: '24px' }}>
        <SectionTitle icon={Briefcase} title="직무" />
        <InfoRow label="직위" value={displayValue(profile.positionTitle)} />
        <InfoRow label="직책" value={displayValue(profile.jobTitle)} />
      </section>

      {/* 3) 고용/계약 */}
      <section style={{ marginBottom: '24px' }}>
        <SectionTitle icon={Calendar} title="고용/계약" />
        <InfoRow label="고용형태" value={displayValue(profile.currentEmploymentType)} />
        <InfoRow label="재직상태" value={getStatusLabel(profile.status)} />
        <InfoRow label="최초입사일" value={displayValue(profile.firstHireDate)} />
        {/* V62: 파견직은 엔글 최초입사일 미노출 */}
        {profile.currentEmploymentTypeCode !== 'DISPATCH' && (
          <InfoRow label="엔글 최초 입사일" value={displayValue(profile.ngleFirstHireDate)} />
        )}
        <InfoRow label="수습 만료일" value={displayValue(profile.probationEndDate)} />
        <InfoRow label="정규직 전환일" value={displayValue(profile.regularHireDate)} />
      </section>

      {/* 4) 학력/기타 */}
      <section style={{ marginBottom: '24px' }}>
        <SectionTitle icon={GraduationCap} title="학력/기타" />
        <InfoRow label="학력" value={getEducationLevelLabelSafe(profile.educationLevel)} />
        <InfoRow label="전공" value={displayValue(profile.major)} />
        <InfoRow label="자격증" value={displayValue(profile.certificatesText)} />
        <InfoRow label="교육이수" value={displayValue(profile.trainingsText)} />
        <InfoRow label="비고" value={displayValue(profile.commentText)} />
      </section>

      {/* 5) 연락처 */}
      <section>
        <SectionTitle icon={Phone} title="연락처" />
        <InfoRow label="전화번호" value={displayValue(profile.phoneNumber)} />
        <InfoRow label="주소" value={displayValue(profile.address)} />
      </section>
    </div>
  );
}

/**
 * OrgMemberProfileModal 컴포넌트
 */
export function OrgMemberProfileModal({
  isOpen,
  targetUserId,
  onClose,
}: OrgMemberProfileModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const { profile, isLoading, error, fetchProfile, reset } = useOrgMemberProfile();

  // Modal 오픈 시 데이터 fetch
  useEffect(() => {
    if (isOpen && targetUserId) {
      fetchProfile(targetUserId);
    } else if (!isOpen) {
      reset();
    }
  }, [isOpen, targetUserId, fetchProfile, reset]);

  // ESC 키로 닫기
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay (배경 클릭으로 닫기) */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
        }}
        {...overlayProps}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '480px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          zIndex: 1001,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            margin: 0,
          }}>
            직원 상세 정보
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-100)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} color="var(--color-gray-500)" />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: '20px',
        }}>
          {/* 로딩 */}
          {isLoading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 24px',
              gap: '12px',
            }}>
              <Loader2 size={32} color="var(--color-primary)" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>
                정보를 불러오는 중...
              </span>
            </div>
          )}

          {/* 에러 */}
          {!isLoading && error && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 24px',
              gap: '12px',
            }}>
              <AlertCircle size={32} color="var(--color-danger)" />
              <span style={{
                fontSize: '14px',
                color: 'var(--color-gray-600)',
                textAlign: 'center',
              }}>
                {error}
              </span>
            </div>
          )}

          {/* 프로필 내용 */}
          {!isLoading && !error && profile && (
            <ProfileContent profile={profile} />
          )}
        </div>
      </div>

      {/* 스피너 애니메이션 */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
